import React, { FC, memo, useState, FormEvent, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { white } from 'theme/colors';
import styled from '@emotion/styled';
import { useTheme } from '@emotion/react';
import { switchProp } from 'styled-tools';
import router from 'next/router';
import { formatString, IMAGE_URL } from '$lib/helpers';
import { useTranslation } from '$hooks';
import { IFindStorePage } from '$models';
import { mq } from '$lib/helpers';
import { Flex } from '$components/layouts';
import { Text } from '$components/elements/text';
import { InputField } from '$components/elements/input-field';
import { Button } from '$components/elements/button';
import { SvgIcon } from '$components/elements/svg-icon';

type Props = IFindStorePage & { onSubmit: (term: string) => void; onReset: () => void; positionAllowed: boolean };

const searchFieldId = 'searchField';

export const StoreMapForm: FC<Props> = memo(
    ({ onSubmit, onReset, heading, backgroundImage, prettySiteName, positionAllowed }) => {
        const [search, setSearch] = useState<string | null>(null);
        const { register, getValues } = useForm();
        const { translate } = useTranslation();
        const theme = useTheme();

        const submit = (event: FormEvent<HTMLFormElement>) => {
            event.preventDefault();
            setSearch(getValues(searchFieldId));
        };

        useEffect(() => {
            if (search === null) {
                return;
            }

            router.push(`${window.location.pathname}${search ? '?search=' + search : ''}`, undefined, {
                shallow: true,
            });

            if (search) {
                onSubmit && onSubmit(search);
            } else {
                onReset && onReset();
            }
        }, [search]);

        useEffect(() => {
            const searchTerm = router.query.search;

            if (searchTerm && typeof searchTerm === 'string') {
                setSearch(searchTerm);
            }
        }, []);

        return (
            <FormContainer backgroundImage={`${IMAGE_URL}${backgroundImage.url}`}>
                <FormInnerContent column>
                    <Text as="h1" bottomMargin="5" size={theme.fontSizes['3xl']} lineHeight="34px" variant="highlight">
                        {heading}
                    </Text>
                    <Text bottomMargin="4" lineHeight="30px" fontWeight="Medium">
                        {formatString(translate('findStore.formDescription'), prettySiteName)}
                    </Text>
                    <StyledForm onSubmit={submit}>
                        <InputWrapper>
                            <InputField
                                type="text"
                                defaultValue={search ? search : undefined}
                                label={translate('findStore.zipOrCity')}
                                {...register(searchFieldId, { minLength: 4 })}
                            />
                        </InputWrapper>
                        <SubmitButton type="submit">
                            <ButtonIcon svg="arrowLeft" rotate={180} size="md" color={white} />
                            {translate('general.search')}
                        </SubmitButton>
                    </StyledForm>
                    <LinkButton
                        variant="link"
                        onClick={() => setSearch('')}
                        state={positionAllowed && search ? 'active' : 'disabled'}
                    >
                        {translate('findStore.useExistingPosition')}
                    </LinkButton>
                </FormInnerContent>
            </FormContainer>
        );
    }
);

const SubmitButton = styled(Button)(({ theme: { space } }) => ({
    paddingRight: space[8],
}));

const ButtonIcon = styled(SvgIcon)(({ theme: { space } }) => ({
    marginRight: space[4],
}));

const LinkButton = styled(Button)(
    ({ theme: { colors, fontWeights } }) => ({
        color: colors.red,
        textDecoration: 'none',
        fontWeight: fontWeights.regular,
        transition: 'opacity 200ms ease',
        alignSelf: 'flex-start',
    }),
    switchProp('state', {
        active: {
            opacity: 1,
            pointerEvents: 'all',
        },
        disabled: {
            opacity: 0,
            pointerEvents: 'none',
        },
    })
);

const InputWrapper = styled.div(({ theme: { space } }) => ({
    flex: 1,
    paddingRight: space[4],
    marginBottom: space[4],
}));

const FormContainer = styled.div<{ backgroundImage?: string }>(({ theme: { space } }) => ({ backgroundImage }) => ({
    backgroundImage: `url(${backgroundImage});`,
    backgroundSize: 'cover',
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'left top',
    padding: `${space[3]} ${space[4]}`,
    marginBottom: space[8],
    [mq('lg')]: {
        padding: `${space[10]}`,
        backgroundPosition: 'right top',
    },
}));

const FormInnerContent = styled(Flex)({
    width: '100%',
    [mq('lg')]: {
        width: '45%',
    },
});

const StyledForm = styled.form({
    display: 'flex',
});
